// LinkedRunFields — New run, when the business has linked runs on (mig
// 20261288000000): is this a bulk sort or a packing line, and for a packing
// line, which product it packs and the class and size it takes (its feed).
// One product per line run (Thomas, 2 Nov). A bulk sort names neither; the
// server refuses a product or feed on one (runs_linked_runs_guard).

import { useQuery } from '@tanstack/react-query'
import { GitMerge } from 'lucide-react'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'

export interface LinkedRunChoice {
  kind: 'PACK' | 'BULK_SORT'
  productId: string
  feedGrade: string
  feedSize: string
}

export const EMPTY_LINKED_RUN: LinkedRunChoice = { kind: 'PACK', productId: '', feedGrade: '', feedSize: '' }

/** The runs columns this choice writes. Nothing when linked runs are off, so
 *  every other business inserts exactly what it did before. */
export function linkedRunInsertFields(linkedOn: boolean, c: LinkedRunChoice) {
  if (!linkedOn) return {}
  if (c.kind === 'BULK_SORT') return { run_kind: 'BULK_SORT' as const }
  return {
    run_kind: 'PACK' as const,
    product_id: c.productId || null,
    feed_grade: c.feedGrade.trim() || null,
    feed_size_code: c.feedSize.trim() || null,
  }
}

interface ProductRow { product_id: string; name: string; variant: string | null }

function useLineProducts() {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: ['line-run-products', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      // pallet_pack_products (mig 20261298000000): this business's active
      // products with no prices, readable by a floor seat that starts runs.
      // financial_products itself is managers-only (prices), so a scale
      // operator's line run came up with no product to pick (audit, 2 Nov).
      const { rows, error } = await withReadTimeout(fetchAllPaged<ProductRow>((from, to) =>
        supabase.rpc('pallet_pack_products', {}).order('name').order('product_id').range(from, to) as unknown as PromiseLike<{ data: ProductRow[] | null; error: { message: string } | null }>),
        'New run: line products')
      if (error) throw new Error(error)
      return rows
    },
  })
}

export default function LinkedRunFields({ value, onChange }: {
  value: LinkedRunChoice
  onChange: (next: LinkedRunChoice) => void
}) {
  const productsQ = useLineProducts()
  const set = (patch: Partial<LinkedRunChoice>) => onChange({ ...value, ...patch })
  const kindButton = (kind: LinkedRunChoice['kind'], label: string, hint: string) => (
    <button type="button" role="radio" aria-checked={value.kind === kind}
      onClick={() => set(kind === 'BULK_SORT' ? { ...EMPTY_LINKED_RUN, kind } : { kind })}
      className={clsx('tap-target-y flex-1 rounded-lg border p-2 text-left',
        value.kind === kind ? 'border-teal-500 bg-teal-900/20' : 'border-line bg-surface')}>
      <span className="block text-sm font-semibold text-fg">{label}</span>
      <span className="block text-caption text-fg-secondary">{hint}</span>
    </button>
  )
  return (
    <div className="card space-y-3 border border-teal-700/40">
      <p className="text-sm font-semibold text-fg flex items-center gap-1.5"><GitMerge size={15} /> What kind of run?</p>
      <div role="radiogroup" aria-label="Run kind" className="flex gap-2">
        {kindButton('PACK', 'Packing line', 'Packs one product. Bulk-sort pallets are scanned onto it.')}
        {kindButton('BULK_SORT', 'Bulk sort', 'Sorts delivery pallets into bulk-sort pallets by class and size.')}
      </div>
      {value.kind === 'PACK' && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label className="block sm:col-span-3">
            <span className="text-caption text-fg-secondary">Product this line packs</span>
            <select className="w-full bg-surface text-fg text-sm rounded-lg p-2 border border-line"
              value={value.productId} onChange={e => set({ productId: e.target.value })}>
              <option value="">{productsQ.isError ? 'Products could not load. Pick later.' : 'No product picked'}</option>
              {(productsQ.data ?? []).map(p => (
                <option key={p.product_id} value={p.product_id}>{p.name}{p.variant ? ` · ${p.variant}` : ''}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-caption text-fg-secondary">Class it takes</span>
            <input className="input w-full" placeholder="e.g. 1" value={value.feedGrade}
              onChange={e => set({ feedGrade: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-caption text-fg-secondary">Size it takes</span>
            <input className="input w-full" placeholder="e.g. A" value={value.feedSize}
              onChange={e => set({ feedSize: e.target.value })} />
          </label>
          <p className="text-caption text-fg-muted sm:col-span-3 self-end">
            Optional. A bulk-sort pallet of another class or size gets a warning at Tipping, and is noted if tipped.
          </p>
        </div>
      )}
    </div>
  )
}
